import { useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { cx } from "@/lib/cx";

export interface SwipeAction {
  id: string;
  label: string;
  icon: ReactNode;
  tone: "primary" | "pinned";
  /** Lado da linha de onde a acao e revelada. */
  side: "left" | "right";
  onAction: () => void;
}

const ACTION_WIDTH = 86;
const OPEN_MS = 200;

interface SwipeableRowProps {
  actions: SwipeAction[];
  children: ReactNode;
  className?: string;
}

/**
 * Revela acoes com swipe horizontal (mesmo gesto do Telegram): arrastar para
 * a direita mostra a acao da esquerda, para a esquerda a da direita. O toque
 * vertical continua rolando a lista (`touch-action: pan-y`).
 */
export function SwipeableRow({ actions, children, className }: SwipeableRowProps) {
  const left = actions.filter((a) => a.side === "left");
  const right = actions.filter((a) => a.side === "right");
  const leftWidth = left.length * ACTION_WIDTH;
  const rightWidth = right.length * ACTION_WIDTH;

  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [revealed, setRevealed] = useState<"left" | "right" | null>(null);
  const drag = useRef<{ x: number; y: number; axis: "" | "x" | "y"; moved: boolean } | null>(null);
  const dxRef = useRef(0);

  const apply = (value: number) => {
    dxRef.current = value;
    setDx(value);
  };

  const close = () => {
    setRevealed(null);
    apply(0);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    drag.current = { x: event.clientX, y: event.clientY, axis: "", moved: false };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const deltaX = event.clientX - d.x;
    const deltaY = event.clientY - d.y;
    if (!d.axis) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      d.axis = Math.abs(deltaX) > Math.abs(deltaY) ? "x" : "y";
      if (d.axis === "x") {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          /* ponteiro sintetico sem captura ativa */
        }
        setDragging(true);
      }
    }
    if (d.axis !== "x") return;
    d.moved = true;
    let next = deltaX + (revealed === "left" ? leftWidth : 0) - (revealed === "right" ? rightWidth : 0);
    next = Math.max(-rightWidth, Math.min(leftWidth, next));
    apply(next);
  };

  const endDrag = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.axis !== "x") {
      setDragging(false);
      return;
    }
    setDragging(false);
    const current = dxRef.current;
    if (leftWidth > 0 && current > leftWidth / 2) {
      setRevealed("left");
      apply(leftWidth);
    } else if (rightWidth > 0 && current < -rightWidth / 2) {
      setRevealed("right");
      apply(-rightWidth);
    } else {
      close();
    }
  };

  const onFrontClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (drag.current?.moved) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (revealed) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  const visible = dx !== 0 || revealed !== null;
  const transition = dragging ? "none" : `transform ${OPEN_MS}ms cubic-bezier(0.2, 0, 0, 1)`;

  const renderActions = (list: SwipeAction[], side: "left" | "right") =>
    visible && list.length > 0 ? (
      <div className={cx("swipe-actions", side)} aria-hidden={revealed !== side ? true : undefined}>
        {list.map((action) => (
          <button
            key={action.id}
            type="button"
            className={cx("swipe-action", action.tone)}
            onClick={() => {
              action.onAction();
              close();
            }}
            aria-label={action.label}
            tabIndex={revealed === side ? 0 : -1}
          >
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className={cx("swipe-wrap", className)}>
      {renderActions(left, "left")}
      {renderActions(right, "right")}
      <div
        className="swipe-front"
        style={{ transform: dx !== 0 ? `translate3d(${dx}px, 0, 0)` : undefined, transition }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onFrontClickCapture}
      >
        {children}
      </div>
    </div>
  );
}
